import { useCallback, useMemo, useState } from 'react'

import useSubscriptionSettingsTracking from '../../hooks/useSubscriptionSettingsTracking'

const OTHER_VALUE = 'other'
const OTHER_PREFIX = 'other: '
const MAX_CUSTOM_REASON_LENGTH = 500 - OTHER_PREFIX.length
const PLAN_MONTHLY = 'monthly'

function parseAmount(value) {
  if (typeof value === 'number') return value

  return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0
}

function formatAccessDate(isoDate, locale = 'en') {
  return new Date(isoDate).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function useCancelModal({
  onClose,
  onSubmit,
  subscriptionId,
  tier = null,
  feeData,
  feeError,
  locale,
  subscriptionV16Enabled = false,
  planType = 'yearly',
}) {
  const [selectedReason, setSelectedReason] = useState(null)
  const [otherText, setOtherText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAcknowledged, setIsAcknowledged] = useState(false)
  const { trackCancellationConfirmed } = useSubscriptionSettingsTracking(tier)

  const isOtherSelected = selectedReason?.value === OTHER_VALUE

  // Plan-type fact, independent of the flag: a monthly plan never has an early-cancellation fee,
  // so flag-off monthly subscribers must not hit the fee path (no fee modal, no fee charge).
  const isMonthly = planType === PLAN_MONTHLY

  const feeBreakdown = useMemo(() => {
    if (!feeData) return null

    const cancelFee = parseAmount(feeData.cancel_fee)
    if (!cancelFee) return null

    return {
      remainingMonths: feeData.remaining_months,
      monthlyRate: parseAmount(feeData.monthly_rate),
      totalRemaining: parseAmount(feeData.total_remaining),
      cancelFee,
    }
  }, [feeData])

  const hasFee = Boolean(feeBreakdown) && !isMonthly

  const showReasonSelect = !hasFee || subscriptionV16Enabled
  const reasonSelectPosition = hasFee ? 'top' : 'below-banner'

  const accessUntilFormatted = useMemo(() => {
    if (!feeData) return null

    return formatAccessDate(feeData.access_until, locale)
  }, [feeData, locale])

  const handleClose = useCallback(() => {
    setSelectedReason(null)
    setOtherText('')
    setIsSubmitting(false)
    setIsAcknowledged(false)
    onClose()
  }, [onClose])

  const handleReasonChange = useCallback((option) => {
    setSelectedReason(option)

    if (option?.value !== OTHER_VALUE) {
      setOtherText('')
    }
  }, [])

  const handleOtherTextChange = useCallback((e) => {
    setOtherText(e.target.value)
  }, [])

  const submitAndTrack = useCallback(async (reason, trackingArgs) => {
    setIsSubmitting(true)

    try {
      await onSubmit(reason)
    } catch {
      setIsSubmitting(false)
      return
    }

    trackCancellationConfirmed(...trackingArgs)
    handleClose()
  }, [onSubmit, trackCancellationConfirmed, handleClose])

  const resolveReasonPayload = useCallback(() => {
    if (!selectedReason) return null

    const isOther = selectedReason.value === OTHER_VALUE
    const customReason = otherText.trim()
    if (isOther && !customReason) return null

    return {
      reason: isOther ? `${OTHER_PREFIX}${customReason}` : selectedReason.value,
      trackingArgs: [
        selectedReason.value,
        subscriptionId,
        isOther ? customReason : undefined,
      ],
    }
  }, [selectedReason, otherText, subscriptionId])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    if (hasFee && !isAcknowledged) return

    if (showReasonSelect) {
      const payload = resolveReasonPayload()
      if (!payload) return

      const reason = hasFee
        ? `early_cancellation_fee: ${payload.reason}`
        : payload.reason
      const trackingArgs = hasFee
        ? ['early_cancellation_fee', subscriptionId, payload.reason]
        : payload.trackingArgs

      await submitAndTrack(reason, trackingArgs)
      return
    }

    await submitAndTrack(
      'early_cancellation_fee',
      ['early_cancellation_fee', subscriptionId]
    )
  }, [
    hasFee,
    isAcknowledged,
    isSubmitting,
    showReasonSelect,
    resolveReasonPayload,
    submitAndTrack,
    subscriptionId,
  ])

  const isReasonMissing = showReasonSelect && (!selectedReason || (isOtherSelected && !otherText.trim()))
  const isAckMissing = hasFee && !isAcknowledged
  const isSubmitDisabled = !!feeError || isSubmitting || isReasonMissing || isAckMissing

  return {
    feeBreakdown,
    hasFee,
    feeError,
    showReasonSelect,
    reasonSelectPosition,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    maxCustomReasonLength: MAX_CUSTOM_REASON_LENGTH,
  }
}

export default useCancelModal
